import { useNavigate } from "@tanstack/react-router";
import { PencilIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";

import { BillingErrorState } from "../../components/BillingErrorState";
import { CurrentPlanCard } from "../../components/overview/components/CurrentPlanCard";
import { RecentInvoicesSection } from "../../components/overview/components/RecentInvoicesSection";
import { UpcomingPaymentCard } from "../../components/overview/components/UpcomingPaymentCard";
import { AdjustRenewalDateModal } from "../credits/AdjustRenewalDateModal";
import { IssueCreditModal } from "../credits/IssueCreditModal";
import { AccountDetailsCard } from "./components/AccountDetailsCard";
import { AccountPaymentMethodsCard } from "./components/AccountPaymentMethodsCard";
import { AdminOverviewSkeleton } from "./components/AdminOverviewSkeleton";
import { CouponsCard } from "./components/CouponsCard";
import { CreditBalanceCard } from "./components/CreditBalanceCard";
import { useBillingAccountOverview } from "./hooks/useBillingAccountOverview";
import {
  ADMIN_OVERVIEW_GRID,
  ADMIN_RECENT_INVOICES_CELL,
  ADMIN_SIDE_COLUMN,
} from "./utils/admin-overview-grid";

interface AdminOverviewTabProps {
  readonly projectId: number;
}

export function AdminOverviewTab({ projectId }: AdminOverviewTabProps) {
  const { data, isError, isPending, refetch } =
    useBillingAccountOverview(projectId);
  const navigate = useNavigate();
  const [creditInvoiceId, setCreditInvoiceId] = useState<null | string>(null);
  const [isAdjustingRenewalDate, setIsAdjustingRenewalDate] = useState(false);

  if (isPending) {
    return <AdminOverviewSkeleton />;
  }

  if (isError) {
    return (
      <BillingErrorState
        message="Failed to load the account overview."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className={ADMIN_OVERVIEW_GRID}>
      <CurrentPlanCard data={data} />
      <UpcomingPaymentCard
        data={data}
        renewalDateAction={
          <Button
            className="flex cursor-pointer items-center gap-xs self-start text-sm font-semibold text-button-tertiary-color-fg hover:text-button-tertiary-color-fg-hover"
            onPress={() => setIsAdjustingRenewalDate(true)}
            variant="unstyled"
          >
            <PencilIcon size={16} />
            Adjust renewal date
          </Button>
        }
        showDiscount={false}
      />
      <CreditBalanceCard creditBalance={data.creditBalance} />
      <CouponsCard coupons={data.coupons} projectId={projectId} />

      <div className={ADMIN_RECENT_INVOICES_CELL}>
        <RecentInvoicesSection
          limit={4}
          onIssueCredit={(invoice) => setCreditInvoiceId(invoice.id)}
          onSeeAll={() =>
            navigate({
              params: { projectId: String(projectId) },
              to: "/billing/accounts/$projectId/invoices",
            })
          }
          projectId={projectId}
        />
      </div>

      <div className={ADMIN_SIDE_COLUMN}>
        <AccountDetailsCard account={data.account} />
        <AccountPaymentMethodsCard projectId={projectId} />
      </div>

      {isAdjustingRenewalDate && (
        <AdjustRenewalDateModal
          onClose={() => setIsAdjustingRenewalDate(false)}
          projectId={projectId}
        />
      )}

      {creditInvoiceId && (
        <IssueCreditModal
          invoiceId={creditInvoiceId}
          onClose={() => setCreditInvoiceId(null)}
          projectId={projectId}
        />
      )}
    </div>
  );
}
